import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SESSION_EXPIRED_EVENT, resetSessionExpiry } from '../config/session';
import { AUTH_EVENT, isSignedIn } from './useAuthStorage';
import { useShopUI } from '../context/ShopUIContext';

/**
 * Turns an expired session into something the user can see and act on:
 * an alert, a bounce off any protected page, and the login modal.
 *
 * The credentials themselves are already cleared by `expireSession`;
 * this hook only owns the UI half so nothing outside React has to know
 * about the router or the modals.
 */
const useSessionExpiry = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openLogin } = useShopUI();

  useEffect(() => {
    const handleExpired = async () => {
      // `/check/*` pages are useless without a session, so leave them
      // before the alert rather than after the user dismisses it.
      if (pathname.startsWith('/check')) navigate('/', { replace: true });

      await Swal.fire({
        icon: 'warning',
        title: 'You are logged out',
        text: 'Please login again.',
        confirmButtonColor: '#a17e33',
        allowOutsideClick: false,
      });

      openLogin();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired);
  }, [navigate, pathname, openLogin]);

  // A fresh login re-arms the guard, which otherwise alerts only once
  // per page load.
  useEffect(() => {
    const handleAuthChange = () => {
      if (isSignedIn()) resetSessionExpiry();
    };

    window.addEventListener(AUTH_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, handleAuthChange);
  }, []);
};

export default useSessionExpiry;
